
import { useEffect, useState } from "react";
import { getPosts, Post } from "@/lib/supabase";
import BlogPostCard from "@/components/BlogPostCard";
import FeaturedPosts from "@/components/FeaturedPosts";
import AdSense from "@/components/AdSense";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Archive } from "lucide-react";
import SubscriptionSection from "@/components/SubscriptionSection";
import { initializeAdSense, getDeviceType } from "@/lib/utils";

const Home = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  
  useEffect(() => {
    const fetchPosts = async () => {
      setIsLoading(true);
      const data = await getPosts();
      setPosts(data);
      setIsLoading(false);
    };
    
    fetchPosts();
    
    // Initialize AdSense on page load with the correct client ID
    initializeAdSense();
    
    // Set initial device type
    setDeviceType(getDeviceType());
    
    // Update device type on window resize
    const handleResize = () => setDeviceType(getDeviceType());
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  return (
    <div className="container py-4 md:py-8 max-w-screen-xl relative px-4 md:px-6">
      {/* Enhanced hero section with futuristic styling */}
      <div className="mb-8 md:mb-12 text-center relative">
        <div className="absolute inset-0 bg-gradient-primary opacity-5 blur-3xl rounded-full -z-10"></div>
        <h1 className="mb-4 md:mb-6 text-4xl sm:text-5xl md:text-6xl font-bold text-blog-neon futuristic-title">
          <span className="animated-title">AI Genesis</span>
        </h1>
        <p className="mx-auto max-w-2xl text-lg md:text-xl text-muted-foreground my-0 py-[8px] px-2">
          Exploring the frontiers of artificial intelligence and its applications in our everyday lives.
        </p>
      </div>

      <FeaturedPosts />

      {isLoading ? (
        <div className="flex justify-center py-8 md:py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blog-neon border-t-transparent shadow-[0_0_10px_rgba(67,255,255,0.5)]"></div>
        </div>
      ) : posts.length > 0 ? (
        <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
          {posts.map(post => <BlogPostCard key={post.id} post={post} />)}
        </div>
      ) : (
        <div className="py-8 md:py-12 text-center glass-card p-4 md:p-8">
          <h2 className="text-xl md:text-2xl font-medium text-gradient">No posts found</h2>
          <p className="mt-2 text-muted-foreground">Check back soon for new content!</p>
        </div>
      )}
      
      {/* Subscribe section */}
      <div className="mt-12 md:mt-16">
        <SubscriptionSection />
      </div>
      
      {/* Browse archives section */}
      <div className="mt-12 md:mt-16 mb-8 md:mb-12 flex flex-col items-center justify-center">
        <div className="glass-card p-4 md:p-8 text-center max-w-2xl mx-auto border-futuristic-border/40">
          <Archive className="h-8 md:h-12 w-8 md:w-12 mx-auto text-blog-neon mb-3 md:mb-4" />
          <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-3">Explore Our Archives</h2>
          <p className="text-muted-foreground mb-4 md:mb-6">
            Browse our complete collection of articles chronologically. Discover insights from our past publications.
          </p>
          <Link to="/archive">
            <Button className="bg-blog-neon hover:bg-blog-neon/90">
              View Archives
            </Button>
          </Link>
        </div>
      </div>
      
      {/* AdSense section with updated client ID */}
      <div className="mt-12 md:mt-16 mb-8">
        <div className="relative py-6 md:py-8 px-3 md:px-4 glass-card border-futuristic-border/40">
          <AdSense 
            slot="1234567890"
            format="auto"
            responsive={true}
            className="min-h-[90px] md:min-h-[250px]"
          />
        </div>
      </div>
    </div>
  );
};

export default Home;
